/**
 * API Keys Management Page
 *
 * Main page for managing API keys with filtering, pagination, and key operations.
 * Provides access to key creation, rotation, rate limit updates, and deactivation.
 */

import { useState } from 'react';
import { Plus, Key, Smartphone, Globe, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { PaginationControls } from '@/components/pagination-controls';
import {
  ApiKeysTable,
  ApiKeyFilters,
  ApiKeyCreateDialog,
  ApiKeyDetailModal,
} from '@/components/api-keys';
import { useApiKeys, useRotationSummary } from '@/hooks/use-api-keys';
import type { ClientType, ApiKey } from '@/types/api-key';

/**
 * API Keys Page Component
 */
export function Component() {
  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  // Filter state
  const [clientTypeFilter, setClientTypeFilter] = useState<ClientType | undefined>(undefined);
  const [statusTab, setStatusTab] = useState<'active' | 'inactive'>('active');

  // Derive isActive filter from status tab
  const isActiveFilter = statusTab === 'active';

  // Dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedKeyId, setSelectedKeyId] = useState<string | null>(null);

  // Selection state for bulk operations
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

  // Calculate offset from page
  const offset = (page - 1) * limit;

  // Fetch API keys with current filters
  const {
    data: keysData,
    isLoading,
    error,
    refetch,
  } = useApiKeys({
    limit,
    offset,
    clientType: clientTypeFilter,
    isActive: isActiveFilter,
  });

  // Fetch rotation summary for stats
  const { data: rotationSummary } = useRotationSummary();

  // Extract data
  const apiKeys = keysData?.data || [];
  const pagination = keysData?.pagination;

  // Calculate total pages
  const totalPages = pagination ? Math.ceil(pagination.total / limit) : 0;

  /**
   * Handle Key Click
   * Opens detail modal for the selected key
   */
  const handleKeyClick = (apiKey: ApiKey) => {
    setSelectedKeyId(apiKey.id);
  };

  /**
   * Handle Close Modal
   */
  const handleCloseModal = () => {
    setSelectedKeyId(null);
  };

  /**
   * Handle Page Change
   */
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setSelectedKeys([]); // Clear selections on page change
  };

  /**
   * Handle Limit Change
   */
  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1); // Reset to page 1 when limit changes
    setSelectedKeys([]); // Clear selections on limit change
  };

  /**
   * Handle Filter Change
   */
  const handleFilterChange = (filters: { clientType?: ClientType }) => {
    setClientTypeFilter(filters.clientType);
    setPage(1); // Reset to page 1 when filters change
    setSelectedKeys([]); // Clear selections on filter change
  };

  /**
   * Handle Clear Filters
   */
  const handleClearFilters = () => {
    setClientTypeFilter(undefined);
    setPage(1);
    setSelectedKeys([]);
  };

  /**
   * Handle Tab Change
   */
  const handleTabChange = (value: string) => {
    setStatusTab(value as 'active' | 'inactive');
    setPage(1); // Reset to page 1 when tab changes
    setSelectedKeys([]); // Clear selections on tab change
  };

  /**
   * Handle Selection Change
   */
  const handleSelectionChange = (ids: string[]) => {
    setSelectedKeys(ids);
  };

  // Calculate summary statistics from rotation summary
  const totalKeys = rotationSummary?.data?.total || pagination?.total || 0;
  const webStats = rotationSummary?.data?.web;
  const mobileStats = rotationSummary?.data?.mobile;
  const expiringSoonCount = (webStats?.expiringSoon || 0) + (mobileStats?.expiringSoon || 0);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">API Keys</h1>
          <p className="text-muted-foreground mt-2">
            Manage API keys for client authentication, rate limiting, and access control
          </p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Create API Key
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <Key className="h-4 w-4" />
              Total Keys
            </CardDescription>
            <CardTitle className="text-4xl">{totalKeys}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              {clientTypeFilter ? `Filtered by ${clientTypeFilter}` : 'All client types'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <Globe className="h-4 w-4" />
              Web Keys
            </CardDescription>
            <CardTitle className="text-4xl">{webStats?.total || 0}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              {webStats?.active || 0} active, {webStats?.expired || 0} expired
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <Smartphone className="h-4 w-4" />
              Mobile Keys
            </CardDescription>
            <CardTitle className="text-4xl">{mobileStats?.total || 0}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              {mobileStats?.active || 0} active, {mobileStats?.expired || 0} expired
            </p>
          </CardContent>
        </Card>

        <Card className={expiringSoonCount > 0 ? 'border-orange-500' : ''}>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <AlertTriangle
                className={`h-4 w-4 ${expiringSoonCount > 0 ? 'text-orange-500' : ''}`}
              />
              Expiring Soon
            </CardDescription>
            <CardTitle className={`text-4xl ${expiringSoonCount > 0 ? 'text-orange-500' : ''}`}>
              {expiringSoonCount}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">Keys expiring within 30 days</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Card>
        <CardHeader>
          <CardTitle>API Keys</CardTitle>
          <CardDescription>
            Click on a key to view details, update rate limits, or rotate
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Tabs for Active/Inactive Status */}
          <Tabs value={statusTab} onValueChange={handleTabChange}>
            <TabsList>
              <TabsTrigger value="active">Active Keys</TabsTrigger>
              <TabsTrigger value="inactive">Inactive Keys</TabsTrigger>
            </TabsList>

            <TabsContent value={statusTab} className="space-y-4">
              {/* Filters */}
              <ApiKeyFilters
                clientType={clientTypeFilter}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
              />

              {/* Keys Table */}
              <ApiKeysTable
                apiKeys={apiKeys}
                isLoading={isLoading}
                error={error}
                onRetry={refetch}
                onKeyClick={handleKeyClick}
                selectedKeys={selectedKeys}
                onSelectionChange={handleSelectionChange}
              />

              {/* Pagination */}
              {pagination && totalPages > 1 && (
                <PaginationControls
                  pagination={{
                    page,
                    limit,
                    total: pagination.total,
                    totalPages,
                  }}
                  onPageChange={handlePageChange}
                  onLimitChange={handleLimitChange}
                />
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <ApiKeyCreateDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSuccess={() => {
          refetch();
        }}
      />

      {/* Detail Modal */}
      <ApiKeyDetailModal keyId={selectedKeyId} onClose={handleCloseModal} />
    </div>
  );
}

// Set display name for React Router
Component.displayName = 'ApiKeysPage';
